import { useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { formatMoney } from '@/lib/utils';
import { getPayrollPayment, payrollPaymentQueryKey } from '@/lib/api/payroll';

interface Props {
  businessId: string;
  paymentId: string;
  onClose: () => void;
}

export function PaymentDetailModal({ businessId, paymentId, onClose }: Props) {
  const { t } = useLanguage();

  const paymentQuery = useQuery({
    queryKey: payrollPaymentQueryKey(businessId, paymentId),
    queryFn: () => getPayrollPayment(businessId, paymentId),
  });

  const payment = paymentQuery.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">{t.payrollPaymentDetailTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="payment-detail-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {paymentQuery.isPending ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">{t.loading}</p>
        ) : paymentQuery.isError || !payment ? (
          <p className="px-6 py-8 text-sm text-destructive">{t.payrollLoadError}</p>
        ) : (
          <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t.payrollPaidAt}</span>
              <span className="font-medium">{new Date(payment.paid_at).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t.payrollTotalOwed}</span>
              <span className="font-semibold">{formatMoney(payment.total_amount)}</span>
            </div>
            {payment.note && (
              <div className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">{t.payrollNotePlaceholder}</span>
                <span>{payment.note}</span>
              </div>
            )}

            <ul className="flex flex-col gap-1 pt-2 border-t">
              {payment.items.map((item, index) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between text-xs py-1.5"
                  data-testid={`payment-detail-item-${item.id}`}
                >
                  <span className="text-muted-foreground">
                    {t.payrollLineItem} {index + 1}
                  </span>
                  <span className="font-medium">{formatMoney(item.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
